/**
 * AiBookingPanel — admin AI Booking Assistant (Phase 1, draft-only).
 *
 * The dispatcher types a natural-language booking request; the AI extracts a
 * structured DRAFT (it never books or dispatches). When the draft has the
 * essentials, the dispatcher opens the EXISTING New Booking modal (pre-filled),
 * confirms the addresses via Google autocomplete, and books through the normal
 * /admin/bookings flow. Channel-independent backend, admin surface only.
 */
import React, { useState, useRef, useEffect } from "react";
import { Wand2, Send, Loader2, User, Phone, MapPin, Clock, CreditCard, Users, Accessibility, RotateCcw, CheckCircle2, AlertCircle, Plane, TrendingUp, Moon, Dog, PlusCircle, PlaneTakeoff, Timer, Route, Car, ChevronDown, ChevronRight, Inbox, Globe, X, Hand, UserCheck, History, HelpCircle, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { api, formatApiError } from "../../lib/api";
import { forwardGeocode } from "../../components/PlaceAutocomplete";
import { NewBookingModal } from "./NewBookingModal";
import { toast } from "sonner";

const BADGE_ICONS = { airport: Plane, surge: TrendingUp, night: Moon, pet: Dog, stop: PlusCircle, terminal: PlaneTakeoff, wait: Timer };
const VEHICLE_LABEL = { car: "Standard Saloon", bus: "Minibus" };
const gbp = (v) => `£${Number(v || 0).toFixed(2)}`;

/** Build the read-only fare component rows from the existing estimate breakdown. */
const fareLines = (quote) => {
  const bd = quote.breakdown || {};
  const ex = bd.extras || {};
  const lines = [];
  lines.push([quote.is_fixed_route ? "Fixed route fare" : "Meter fare", gbp(bd.meter_total)]);
  if ((ex.extra_stops_total || 0) > 0) lines.push([`Extra stops${ex.extra_stops_count ? ` (${ex.extra_stops_count})` : ""}`, gbp(ex.extra_stops_total)]);
  if ((ex.dog_total || 0) > 0) lines.push(["Pet charge", gbp(ex.dog_total)]);
  if ((ex.terminal_total || 0) > 0) lines.push(["Terminal fee", gbp(ex.terminal_total)]);
  if ((bd.late_night_amount || 0) > 0) lines.push(["Late night uplift", gbp(bd.late_night_amount)]);
  return lines;
};

const newSessionId = () =>
  (window.crypto?.randomUUID?.() || `aib-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const pad = (n) => String(n).padStart(2, "0");

const buildInitial = (draft) => {
  let date = "", time = "", scheduled = false;
  if (draft.scheduled_for && draft.scheduled_for !== "asap") {
    const d = new Date(draft.scheduled_for);
    if (!isNaN(d.getTime())) {
      scheduled = true;
      date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
  }
  const noteParts = [draft.accessibility, draft.notes].filter(Boolean);
  return {
    name: draft.customer_name || "",
    phone: draft.customer_phone || "",
    pickup: draft.pickup_text ? { name: draft.pickup_text, lat: null, lng: null } : null,
    dropoff: draft.dropoff_text ? { name: draft.dropoff_text, lat: null, lng: null } : null,
    payment: draft.payment_method || "cash",
    passenger_count: draft.passenger_count || 1,
    notes: noteParts.join(" · "),
    scheduled, date, time,
  };
};

const SCHEDULE_LABEL = (s) => {
  if (!s || s === "asap") return "ASAP";
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const DraftField = ({ icon: Icon, label, value, missing }) => (
  <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${missing ? "border-dashed border-[#F59E0B] bg-[#FFFBEB]" : "border-border bg-white"}`}>
    <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${missing ? "text-[#B45309]" : "text-muted-foreground"}`} />
    <div className="min-w-0">
      <div className="label-mini text-muted-foreground">{label}</div>
      <div className={`text-sm truncate ${missing ? "text-[#B45309] italic" : "font-medium"}`}>{missing ? "Needed" : (value || "—")}</div>
    </div>
  </div>
);

const EXAMPLES = [
  "Book John from Basingstoke Station to Heathrow tomorrow at 5am.",
  "Wheelchair-accessible taxi from RG24 9FJ to North Hampshire Hospital, pay by card.",
  "Return airport booking for Mary Smith, 07700 900456, on Friday at 9am.",
];

const PROVIDER_META = {
  google_places: { label: "Google", cls: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]", Icon: Globe },
  llm: { label: "AI fallback", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]", Icon: Sparkles },
};
const providerMeta = (p) => PROVIDER_META[p] || { label: p || "—", cls: "bg-secondary text-muted-foreground border-border", Icon: HelpCircle };

const POI_STATUS = {
  confirm: { label: "Confirmed match", cls: "text-[#166534]", Icon: CheckCircle2 },
  choose: { label: "Multiple matches", cls: "text-[#B45309]", Icon: HelpCircle },
  need_detail: { label: "Not found — asked for detail", cls: "text-[#991B1B]", Icon: AlertCircle },
};
const CHANNEL_LABEL = { admin: "Admin", website: "Website", passenger: "Passenger", voice: "Phone" };

const poiTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/** Read-only audit list of AI POI/location resolutions. No booking actions. */
const PoiResolutionLog = ({ entries, open, onToggle, onRefresh }) => (
  <div className="mt-4 border border-border rounded-xl bg-white overflow-hidden" data-testid="poi-log-panel">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
      data-testid="poi-log-toggle"
      aria-expanded={open}
    >
      <span className="flex items-center gap-2 font-bold text-foreground">
        <History className="w-4 h-4 text-[#002FA7]" /> AI location resolutions
        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[11px] font-black rounded-full bg-[#E0E7FF] text-[#3730A3]" data-testid="poi-log-count">
          {entries.length}
        </span>
        <span className="text-[11px] font-normal text-muted-foreground hidden sm:inline">read-only audit — spot a wrong place before booking</span>
      </span>
      {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
    </button>
    {open && (
      <div className="px-3 pb-3">
        <div className="flex justify-end mb-2">
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="poi-log-refresh">
            <RotateCcw className="w-3 h-3 mr-1.5" /> Refresh
          </Button>
        </div>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground px-1 py-4 text-center" data-testid="poi-log-empty">
            No place lookups yet. When a caller names a place (not a full address), the resolution appears here.
          </p>
        ) : (
          <div className="space-y-2" data-testid="poi-log-list">
            {entries.map((e, i) => {
              const pm = providerMeta(e.provider);
              const sm = POI_STATUS[e.status] || POI_STATUS.need_detail;
              const PIcon = pm.Icon, SIcon = sm.Icon;
              return (
                <div key={i} className="border border-border rounded-lg p-3 bg-[#FAFAFB]" data-testid={`poi-log-row-${i}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                        {e.field === "pickup" ? "Pickup said" : "Destination said"}
                        <span className="text-muted-foreground/40">·</span>
                        {CHANNEL_LABEL[e.channel] || e.channel}
                      </div>
                      <div className="text-sm font-semibold text-foreground mt-0.5 flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="truncate" data-testid={`poi-log-spoken-${i}`}>“{e.spoken}”</span>
                      </div>
                      {e.resolved ? (
                        <div className="text-sm text-[#166534] mt-1 flex items-center gap-1.5">
                          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate" data-testid={`poi-log-resolved-${i}`}>{e.resolved}</span>
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground italic mt-1">No confident match returned.</div>
                      )}
                      {(e.candidates?.length || 0) > 1 && (
                        <div className="text-xs text-muted-foreground mt-1 pl-5">
                          {e.candidates.length} options: {e.candidates.map((c) => c.display).filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${pm.cls}`} data-testid={`poi-log-provider-${i}`}>
                        <PIcon className="w-3 h-3" /> {pm.label}
                      </span>
                      {typeof e.confidence === "number" && (
                        <span className="text-[10px] font-mono-tab text-muted-foreground" data-testid={`poi-log-confidence-${i}`}>
                          {Math.round(e.confidence * 100)}% conf.
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">{poiTime(e.created_at)}</span>
                    </div>
                  </div>
                  <div className={`mt-2 pt-2 border-t border-dashed border-border text-xs font-semibold flex items-center gap-1.5 ${sm.cls}`} data-testid={`poi-log-status-${i}`}>
                    <SIcon className="w-3.5 h-3.5" /> {sm.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}
  </div>
);

export const AiBookingPanel = ({ onCreateBooking, focusRequest, currentUser, ackSignal }) => {
  const [sessionId, setSessionId] = useState(newSessionId);
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) sessionIdRef.current = sessionId;
  const resetSession = () => {
    const id = newSessionId();
    sessionIdRef.current = id;   // update synchronously so the next /parse uses it
    setSessionId(id);
    return id;
  };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState(null);   // { missing_fields, ready_for_review, confidence }
  const [modalOpen, setModalOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const scrollRef = useRef(null);

  // ── Web Requests queue (Phase 2) — customer-submitted website AI bookings
  // awaiting a dispatcher's review. Read-only poll; booking still happens
  // through the existing /admin/bookings flow (human-in-the-loop).
  const [webRequests, setWebRequests] = useState([]);
  const [webOpen, setWebOpen] = useState(true);
  const [webReqModal, setWebReqModal] = useState(null);
  const [highlightSid, setHighlightSid] = useState(null);
  const webCardRefs = useRef({});

  // ── AI location resolutions (read-only audit) — what callers said vs. the
  // place the resolver returned + provider (Google / LLM). Helps a dispatcher
  // spot a wrong POI before booking. No dispatch/lifecycle interaction.
  const [poiLog, setPoiLog] = useState([]);
  const [poiLogOpen, setPoiLogOpen] = useState(false);

  const refreshPoiLog = async () => {
    try {
      const { data } = await api.get("/admin/ai-booking/poi-log?limit=40");
      setPoiLog(data.entries || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => {
    refreshPoiLog();
    const t = setInterval(refreshPoiLog, 20000);
    return () => clearInterval(t);
  }, []);

  const refreshWebRequests = async () => {
    try {
      const { data } = await api.get("/admin/ai-booking/web-requests");
      setWebRequests(data.requests || []);
    } catch { /* non-fatal — queue just stays as-is */ }
  };

  useEffect(() => {
    refreshWebRequests();
    const t = setInterval(refreshWebRequests, 20000);
    return () => clearInterval(t);
  }, []);

  // When the Control Room toast's "Review request" is clicked, refresh the
  // queue, expand it, and scroll/highlight the requested card.
  useEffect(() => {
    if (!focusRequest?.session_id) return;
    setWebOpen(true);
    setHighlightSid(focusRequest.session_id);
    refreshWebRequests();
    const t1 = setTimeout(() => {
      webCardRefs.current[focusRequest.session_id]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
    const t2 = setTimeout(() => setHighlightSid(null), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [focusRequest?.ts, focusRequest?.session_id]);

  const dismissWebRequest = async (sid) => {
    try { await api.post(`/admin/ai-booking/web-requests/${sid}/dismiss`); } catch { /* ignore */ }
    refreshWebRequests();
  };

  // Refresh ownership state whenever any dispatcher acknowledges/releases.
  useEffect(() => { if (ackSignal) refreshWebRequests(); }, [ackSignal]);

  const acknowledgeRequest = async (sid, name, override = false) => {
    try {
      await api.post(`/admin/ai-booking/web-requests/${sid}/acknowledge`, { override });
      toast.success(override ? "You've taken over this request." : `You're now handling ${name || "this request"}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" ? detail.message : detail) || formatApiError(e);
      toast.message(msg);
    }
    refreshWebRequests();
  };

  const openWebRequest = (req) => {
    // Non-blocking ownership notice — a dispatcher can still proceed, but is
    // warned if a colleague already owns the request.
    if (req.acknowledged_by && req.acknowledged_by !== currentUser?.id) {
      toast.message(`This request is currently being handled by ${req.acknowledged_by_name || "another dispatcher"}.`);
    }
    setWebReqModal(req);
  };

  const handleCreateWebReq = async (payload) => {
    const data = await onCreateBooking(payload);   // throws on failure → modal stays open
    const rideId = data?.ride?.id;
    if (rideId && webReqModal?.session_id) {
      api.post(`/admin/ai-booking/session/${webReqModal.session_id}/confirmed`, { ride_id: rideId }).catch(() => {});
    }
    toast.success("Website request booked");
    setWebReqModal(null);
    refreshWebRequests();
    return data;
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  // Live fare/ETA quote — geocodes the AI's pickup/dropoff text and reuses the
  // existing read-only fare estimator. Lets the dispatcher quote a price while
  // still on the phone. The final booking still re-confirms the exact address.
  useEffect(() => {
    const p = draft?.pickup_text, d = draft?.dropoff_text;
    if (!p || !d) { setQuote(null); setQuoteError(null); return; }
    let cancelled = false;
    setQuoteLoading(true); setQuoteError(null);
    (async () => {
      const [pg, dg] = await Promise.all([forwardGeocode(p), forwardGeocode(d)]);
      if (cancelled) return;
      if (!pg || !dg) {
        setQuote(null);
        setQuoteError("Couldn't pin one of the addresses precisely — confirm them on the booking screen for an exact fare.");
        setQuoteLoading(false);
        return;
      }
      try {
        const blob = [draft.notes, draft.accessibility].filter(Boolean).join(" ");
        const hasDog = /\b(dog|pet|puppy|guide dog|assistance dog)\b/i.test(blob);
        const { data } = await api.post("/admin/ai-booking/estimate", {
          pickup_lat: pg.lat, pickup_lng: pg.lng,
          dropoff_lat: dg.lat, dropoff_lng: dg.lng,
          vehicle_type: draft.vehicle_type || "car",
          passenger_count: draft.passenger_count || 1,
          has_dog: hasDog,
        });
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) { setQuote(null); setQuoteError(formatApiError(e)); }
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [draft?.pickup_text, draft?.dropoff_text, draft?.vehicle_type, draft?.passenger_count, draft?.notes, draft?.accessibility]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "dispatcher", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ai-booking/parse", { session_id: sessionIdRef.current, message: msg });
      setDraft(data.draft);
      setMeta({ missing_fields: data.missing_fields, ready_for_review: data.ready_for_review, confidence: data.confidence });
      const reply = data.follow_up_question || data.assistant_message || "Got it.";
      setMessages((m) => [...m, { role: "assistant", text: reply, ready: data.ready_for_review }]);
      refreshPoiLog();
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const startOver = async () => {
    const old = sessionIdRef.current;
    resetSession();
    setMessages([]); setDraft(null); setMeta(null); setInput("");
    setQuote(null); setQuoteError(null);
    try { await api.post(`/admin/ai-booking/session/${old}/reset`); } catch { /* ignore */ }
  };

  const handleCreate = async (payload) => {
    const data = await onCreateBooking(payload);   // throws on failure → modal stays open
    const rideId = data?.ride?.id;
    const bookedSession = sessionIdRef.current;
    if (rideId) {
      api.post(`/admin/ai-booking/session/${bookedSession}/confirmed`, { ride_id: rideId }).catch(() => {});
    }
    toast.success("Booking confirmed from AI draft");
    setModalOpen(false);
    resetSession();
    setMessages([]); setDraft(null); setMeta(null); setQuote(null); setQuoteError(null);
    return data;
  };

  const isMissing = (f) => meta?.missing_fields?.includes(f);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="ai-booking-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-[#002FA7] text-white flex items-center justify-center">
            <Wand2 className="w-5 h-5" />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight leading-none">AI Booking Assistant</h1>
            <p className="text-xs text-muted-foreground mt-1">Type a request in plain English. The AI drafts it — you review &amp; dispatch. Nothing is booked automatically.</p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={startOver} data-testid="ai-booking-reset">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> New
          </Button>
        )}
      </div>

      {/* Web Requests queue — customer-submitted website AI bookings to review */}
      {webRequests.length > 0 && (
        <div className="mb-4 border border-[#C7D2FE] rounded-xl bg-[#EEF2FF] overflow-hidden" data-testid="web-requests-queue">
          <button
            onClick={() => setWebOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left"
            data-testid="web-requests-toggle"
            aria-expanded={webOpen}
          >
            <span className="flex items-center gap-2 font-bold text-[#3730A3]">
              <Inbox className="w-4 h-4" /> Website booking requests
              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-[11px] font-black rounded-full bg-[#4338CA] text-white" data-testid="web-requests-count">
                {webRequests.length}
              </span>
            </span>
            {webOpen ? <ChevronDown className="w-4 h-4 text-[#4338CA]" /> : <ChevronRight className="w-4 h-4 text-[#4338CA]" />}
          </button>
          {webOpen && (
            <div className="px-3 pb-3 space-y-2">
              {webRequests.map((req) => {
                const d = req.draft || {};
                const ownedByMe = req.acknowledged_by && req.acknowledged_by === currentUser?.id;
                const ownedByOther = req.acknowledged_by && req.acknowledged_by !== currentUser?.id;
                return (
                  <div key={req.session_id} ref={(el) => { webCardRefs.current[req.session_id] = el; }} className={`bg-white border rounded-lg p-3 transition-all ${highlightSid === req.session_id ? "border-[#4338CA] ring-2 ring-[#4338CA]/40 shadow-md" : "border-border"}`} data-testid={`web-request-${req.session_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-sm">{d.customer_name || "—"}</span>
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#4338CA] border border-[#C7D2FE]">
                            <Globe className="w-2.5 h-2.5" /> WEB
                          </span>
                          {req.reference && <span className="text-[10px] font-mono-tab text-muted-foreground">{req.reference}</span>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                          <Phone className="w-3 h-3" /> {d.customer_phone || "—"}
                          <span className="text-muted-foreground/40">·</span>
                          <Clock className="w-3 h-3" /> {SCHEDULE_LABEL(d.scheduled_for)}
                        </div>
                        <div className="text-xs mt-1 flex items-start gap-1.5">
                          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-muted-foreground" />
                          <span><span className="font-medium">{d.pickup_text || "—"}</span> → <span className="font-medium">{d.dropoff_text || "—"}</span></span>
                        </div>
                        {req.acknowledged_by && (
                          <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]" data-testid={`web-request-owner-${req.session_id}`}>
                            🟡 Being handled by {ownedByMe ? "you" : (req.acknowledged_by_name || "another dispatcher")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                      <Button size="sm" onClick={() => openWebRequest(req)} className="bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid={`web-request-book-${req.session_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Review &amp; book
                      </Button>
                      {!req.acknowledged_by && (
                        <Button size="sm" variant="outline" onClick={() => acknowledgeRequest(req.session_id, d.customer_name)} className="border-[#F59E0B] text-[#B45309] hover:bg-[#FFFBEB]" data-testid={`web-request-ack-${req.session_id}`}>
                          <Hand className="w-3.5 h-3.5 mr-1.5" /> Acknowledge
                        </Button>
                      )}
                      {ownedByOther && (
                        <Button size="sm" variant="outline" onClick={() => acknowledgeRequest(req.session_id, d.customer_name, true)} className="border-[#4338CA] text-[#4338CA] hover:bg-[#EEF2FF]" data-testid={`web-request-takeover-${req.session_id}`}>
                          <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Take over
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => dismissWebRequest(req.session_id)} data-testid={`web-request-dismiss-${req.session_id}`}>
                        <X className="w-3.5 h-3.5 mr-1.5" /> Dismiss
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-4">
        {/* Conversation */}
        <div className="md:col-span-3 border border-border rounded-xl bg-[#FAFAFB] flex flex-col" style={{ height: "60vh" }}>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-booking-messages">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground mt-6 space-y-3">
                <p>Try one of these:</p>
                {EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => send(ex)}
                    className="block w-full text-left px-3 py-2 rounded-lg border border-border bg-white hover:border-[#002FA7] transition-colors text-foreground"
                    data-testid={`ai-booking-example-${i}`}>
                    “{ex}”
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "dispatcher" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    m.role === "dispatcher"
                      ? "bg-[#002FA7] text-white rounded-br-sm"
                      : m.error ? "bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA] rounded-bl-sm"
                      : "bg-white border border-border rounded-bl-sm"
                  }`}
                  data-testid={`ai-msg-${m.role}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border p-3 flex items-center gap-2 bg-white rounded-b-xl">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="e.g. Book Sarah from RG21 4AE to the hospital at 2pm…"
              className="flex-1 outline-none bg-transparent text-sm px-2"
              data-testid="ai-booking-input"
              disabled={loading}
            />
            <Button onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-booking-send" className="bg-[#002FA7] hover:bg-[#001f6e] text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Draft */}
        <div className="md:col-span-2 space-y-3">
          <div className="border border-border rounded-xl bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mini text-muted-foreground">BOOKING DRAFT</div>
              {meta && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.confidence >= 0.6 ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#FEF3C7] text-[#92400E]"}`} data-testid="ai-booking-confidence">
                  {Math.round((meta.confidence || 0) * 100)}% confident
                </span>
              )}
            </div>

            {!draft ? (
              <p className="text-sm text-muted-foreground">Your structured booking will appear here as you chat.</p>
            ) : (
              <div className="space-y-2" data-testid="ai-booking-draft">
                <DraftField icon={User} label="Passenger" value={draft.customer_name} missing={isMissing("customer_name")} />
                <DraftField icon={Phone} label="Phone" value={draft.customer_phone} missing={isMissing("customer_phone")} />
                <DraftField icon={MapPin} label="Pickup" value={draft.pickup_text} missing={isMissing("pickup_text")} />
                <DraftField icon={MapPin} label="Destination" value={draft.dropoff_text} missing={isMissing("dropoff_text")} />
                <DraftField icon={Clock} label="When" value={SCHEDULE_LABEL(draft.scheduled_for)} />
                <div className="grid grid-cols-2 gap-2">
                  <DraftField icon={Users} label="Passengers" value={String(draft.passenger_count || 1)} />
                  <DraftField icon={CreditCard} label="Payment" value={draft.payment_method || "—"} />
                </div>
                {(draft.accessibility || draft.is_return) && (
                  <DraftField icon={Accessibility} label="Notes" value={[draft.accessibility, draft.is_return ? "Return trip" : null].filter(Boolean).join(" · ")} />
                )}

                {/* Quote sheet — read-only fare/ETA estimate */}
                <div className="pt-2 mt-1 border-t border-dashed border-border" data-testid="ai-booking-quote">
                  {quoteLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Estimating fare &amp; journey…
                    </div>
                  ) : quote ? (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-center my-1">
                        <div className="bg-secondary/50 rounded-lg py-2">
                          <div className="label-mini text-muted-foreground flex items-center justify-center gap-1"><Route className="w-3 h-3" /> DIST</div>
                          <div className="font-bold text-sm mt-0.5" data-testid="quote-distance">{quote.distance_mi} mi</div>
                        </div>
                        <div className="bg-secondary/50 rounded-lg py-2">
                          <div className="label-mini text-muted-foreground flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> TIME</div>
                          <div className="font-bold text-sm mt-0.5" data-testid="quote-duration">{Math.round(quote.duration_min)} min</div>
                        </div>
                        <div className="bg-[#002FA7] text-white rounded-lg py-2">
                          <div className="label-mini text-white/70">EST. FARE</div>
                          <div className="font-heading font-black text-base mt-0.5" data-testid="quote-fare">£{Number(quote.fare).toFixed(2)}</div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="quote-badges">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">
                          <CheckCircle2 className="w-3 h-3" /> {quote.pricing_label}
                        </span>
                        {quote.fare_label && (
                          <span className="text-[11px] text-muted-foreground px-1 py-0.5" data-testid="quote-fare-label">{quote.fare_label}</span>
                        )}
                        {(quote.badges || []).filter((b) => b.label !== quote.pricing_label).map((b, i) => {
                          const Icon = BADGE_ICONS[b.icon] || PlusCircle;
                          return (
                            <span key={i} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">
                              <Icon className="w-3 h-3" /> {b.label}
                            </span>
                          );
                        })}
                      </div>

                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                        <Car className="w-3.5 h-3.5" /> {VEHICLE_LABEL[draft.vehicle_type] || "Standard Saloon"}
                        <span className="ml-auto text-[10px] italic">Estimate · confirm address for exact fare</span>
                      </div>

                      {/* Expandable fare breakdown — read-only, from the estimate response */}
                      <button
                        type="button"
                        onClick={() => setDetailsOpen((v) => !v)}
                        className="mt-2 w-full flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
                        data-testid="quote-details-toggle"
                        aria-expanded={detailsOpen}
                      >
                        {detailsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        Fare details
                      </button>
                      {detailsOpen && quote.breakdown && (
                        <div className="mt-1.5 border border-border rounded-lg p-2.5 space-y-1 bg-secondary/30 text-xs" data-testid="quote-details">
                          {fareLines(quote).map(([label, val], i) => (
                            <div key={i} className="flex justify-between">
                              <span className="text-muted-foreground">{label}</span>
                              <span className="font-mono-tab">{val}</span>
                            </div>
                          ))}
                          <div className="border-t border-border my-1" />
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Net (ex-VAT)</span>
                            <span className="font-mono-tab" data-testid="quote-net">{gbp(quote.breakdown.subtotal_pre_vat_net)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">VAT{quote.breakdown.vat_rate ? ` (${Math.round(quote.breakdown.vat_rate * 100)}%)` : ""}</span>
                            <span className="font-mono-tab" data-testid="quote-vat">{gbp(quote.breakdown.vat_amount)}</span>
                          </div>
                          <div className="flex justify-between font-bold pt-0.5">
                            <span>Total (inc VAT)</span>
                            <span className="font-mono-tab" data-testid="quote-gross">{gbp(quote.breakdown.gross ?? quote.fare)}</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : quoteError ? (
                    <div className="text-[11px] text-muted-foreground italic py-1">{quoteError}</div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          {meta && (
            meta.ready_for_review ? (
              <Button onClick={() => setModalOpen(true)} data-testid="ai-booking-review-confirm"
                className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Review &amp; confirm booking
              </Button>
            ) : (
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2.5" data-testid="ai-booking-not-ready">
                <AlertCircle className="w-4 h-4 mt-0.5 text-[#B45309] flex-shrink-0" />
                <span>I need a little more before this can be booked. {meta.missing_fields?.length ? `Missing: ${meta.missing_fields.map((f) => f.replace("customer_", "").replace("_text", "")).join(", ")}.` : "Please add the missing details above."}</span>
              </div>
            )
          )}
        </div>
      </div>

      {/* AI location resolutions — read-only audit trail */}
      <PoiResolutionLog entries={poiLog} open={poiLogOpen} onToggle={() => setPoiLogOpen((v) => !v)} onRefresh={refreshPoiLog} />

      {modalOpen && draft && (
        <NewBookingModal
          onClose={() => setModalOpen(false)}
          onCreate={handleCreate}
          initial={buildInitial(draft)}
        />
      )}

      {webReqModal && (
        <NewBookingModal
          onClose={() => setWebReqModal(null)}
          onCreate={handleCreateWebReq}
          initial={buildInitial(webReqModal.draft || {})}
        />
      )}
    </div>
  );
};

export default AiBookingPanel;
